// FE 측 chat history 어댑터 — 이전에는 localStorage 였고 이제는 distancing-cowork 의
// /chat/threads/... 를 호출한다 (BE = source of truth). 시그니처는 의도적으로 async 로 바뀜.
//
// signature 변경:
//   loadChat(taskId): PersistedChat              →  loadLatestChat(taskId): Promise<PersistedChat | null>
//   saveChat(taskId, state): void                →  분해: persistUserTurn / persistAssistantTurn / persistThreadMeta
//   clearChat(taskId): void                      →  clearChat(threadId): Promise<boolean>
//
// 호출부 (`useAgentChatThread.ts`) 도 함께 갱신됨.

import type { AssistantStep, AssistantTurn, ChatTurn, UserTurn } from "./types";
import {
  type ChatTurnDTO,
  clearThread,
  getThread,
  listThreads,
  upsertThread,
  upsertTurn,
} from "./chatHistoryApi";

export interface PersistedChat {
  threadId: string | null;
  turns: ChatTurn[];
}

const EMPTY: PersistedChat = { threadId: null, turns: [] };

function assistantPreview(turn: AssistantTurn): string {
  // text step 의 content 를 모아 앞부분만.
  return turn.steps
    .filter(
      (s): s is Extract<AssistantStep, { kind: "text" }> => s.kind === "text"
    )
    .map((s) => s.content)
    .join(" ");
}

function clip(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) : s;
}

function turnToDTOPayload(turn: ChatTurn): Record<string, unknown> {
  // ChatTurn 은 union (user | assistant). FE shape 그대로 jsonb 로.
  return turn as unknown as Record<string, unknown>;
}

function dtoToTurn(dto: ChatTurnDTO): ChatTurn {
  // payload 자체가 ChatTurn shape (FE 가 직접 직렬화). 신뢰하고 캐스팅.
  return dto.payload as unknown as ChatTurn;
}

// =========================
// Load — 마운트 시 1회. 가장 최근(active) thread 의 turns 를 가져온다.
// 0 개면 null (호출자가 새 thread 시작).
// =========================

export async function loadLatestChat(
  taskId: string
): Promise<PersistedChat | null> {
  try {
    const threads = await listThreads(taskId, {
      includeCleared: false,
      limit: 1,
    });
    if (threads.length === 0) return null;
    const latest = await getThread(threads[0].thread_id);
    if (!latest) return null;
    return {
      threadId: latest.thread.thread_id,
      turns: latest.turns.map(dtoToTurn),
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[chatStorage] loadLatestChat failed", err);
    return null;
  }
}

// 특정 thread (cleared 포함) 의 turns. 이전 대화 드롭다운에서 사용.
export async function loadSpecificChat(
  threadId: string
): Promise<PersistedChat | null> {
  try {
    const got = await getThread(threadId);
    if (!got) return null;
    return { threadId: got.thread.thread_id, turns: got.turns.map(dtoToTurn) };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[chatStorage] loadSpecificChat failed", err);
    return null;
  }
}

// =========================
// Upsert — 단일 turn 단위. thread 가 없으면 BE 가 404 → 호출자가 thread 먼저 upsert.
// =========================

export async function persistThreadMeta(
  threadId: string,
  taskId: string,
  patch: {
    title?: string;
    lastUserMessage?: string;
    lastAssistantPreview?: string;
  }
): Promise<void> {
  try {
    await upsertThread(threadId, {
      task_id: taskId,
      title: patch.title,
      last_user_message: patch.lastUserMessage,
      last_assistant_preview: patch.lastAssistantPreview,
    });
  } catch (err) {
    // 영속화 실패는 UI 를 막지 않는다 — best-effort.
    // eslint-disable-next-line no-console
    console.error("[chatStorage] persistThreadMeta failed", err);
  }
}

export async function persistTurn(
  threadId: string,
  taskId: string,
  turn: UserTurn | AssistantTurn,
  sequence: number
): Promise<void> {
  try {
    let result = await upsertTurn(threadId, turn.id, {
      sequence,
      role: turn.role,
      payload: turnToDTOPayload(turn),
    });
    // thread 가 아직 없으면 404 — meta 를 만들고 한 번 더 시도. 첫 user message 의 앞 80자를 title 로.
    if (result === null) {
      const initialTitle =
        turn.role === "user" ? clip(turn.content, 80) : undefined;
      await upsertThread(threadId, { task_id: taskId, title: initialTitle });
      result = await upsertTurn(threadId, turn.id, {
        sequence,
        role: turn.role,
        payload: turnToDTOPayload(turn),
      });
    }
    // 메타 미리보기 갱신 (debounce 없이 매번 — 부하는 router 가 흡수)
    if (turn.role === "user") {
      await upsertThread(threadId, {
        task_id: taskId,
        last_user_message: clip(turn.content, 200),
      });
    } else if (turn.status === "complete" || turn.status === "error") {
      await upsertThread(threadId, {
        task_id: taskId,
        last_assistant_preview: clip(assistantPreview(turn), 200),
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[chatStorage] persistTurn failed", err);
  }
}

// =========================
// Clear — soft delete (is_cleared=true). 이전 대화 드롭다운에서는 여전히 조회 가능.
// =========================

export async function clearChat(threadId: string): Promise<boolean> {
  try {
    return await clearThread(threadId);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[chatStorage] clearChat failed", err);
    return false;
  }
}

// =========================
// 호환성 — 기존 export 가 사라지면 다른 모듈 빌드 깨질 수 있어 더미 보존 (deprecated).
// =========================

export const EMPTY_CHAT: PersistedChat = EMPTY;
